// ABOUTME: The steps of the recruiting process as the design draws them: a round marker per step (a tick once done,
// ABOUTME: else its number), a line down to the next one, the title bold and its hint under it. Job page and application.
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import { Text } from 'components/Text';

export type ProcessStepperState = 'DONE' | 'CURRENT' | 'UPCOMING';

export interface ProcessStepperStep {
  key: string;
  label: string;
  hint?: string | null;
  /** Absent on the job page, where nobody is in the process yet: every step reads as one to come. */
  state?: ProcessStepperState;
}

const MARKER = 32;
// The design's slate for what lies ahead and for the hints; the theme's text.secondary is ink since the rebrand.
const SLATE = 'grey.600';

const MARKER_LOOK = {
  DONE: { bgcolor: 'primary.main', color: 'primary.contrastText' },
  CURRENT: {
    bgcolor: 'background.paper',
    color: 'primary.main',
    border: 2,
    borderColor: 'primary.main'
  },
  UPCOMING: { bgcolor: 'grey.300', color: SLATE }
};

/** The marker: filled with a tick when done, outlined in primary when current, a grey disc with the number ahead. */
const Marker = ({
  number,
  state
}: {
  number: number;
  state?: ProcessStepperState;
}) => (
  <Box
    aria-hidden="true"
    sx={{
      width: MARKER,
      height: MARKER,
      borderRadius: '50%',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...MARKER_LOOK[state ?? 'UPCOMING']
    }}
  >
    {state === 'DONE' ? (
      <CheckIcon sx={{ fontSize: 18 }} />
    ) : (
      <Text variant="caption" color="inherit" sx={{ fontWeight: 700 }}>
        {number}
      </Text>
    )}
  </Box>
);

export const ProcessStepper = ({
  steps,
  testId,
  stepTestId
}: {
  steps: ProcessStepperStep[];
  testId?: string;
  stepTestId?: string;
}) => {
  if (!steps.length) return null;
  return (
    <Box
      component="ol"
      data-testid={testId}
      sx={{ m: 0, p: 0, listStyle: 'none' }}
    >
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const isAhead = step.state !== 'DONE' && step.state !== 'CURRENT';
        return (
          <Box
            component="li"
            key={step.key}
            data-testid={stepTestId}
            data-step-key={step.key}
            data-state={step.state}
            aria-current={step.state === 'CURRENT' ? 'step' : undefined}
            sx={{ display: 'flex', gap: 2 }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Marker number={index + 1} state={step.state} />
              {!isLast && (
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 2,
                    flexGrow: 1,
                    minHeight: 12,
                    my: 0,
                    bgcolor: step.state === 'DONE' ? 'primary.main' : 'grey.300'
                  }}
                />
              )}
            </Box>
            <Box sx={{ minWidth: 0, pt: 0.5, pb: isLast ? 0 : 2 }}>
              <Text
                variant="subtitle1"
                component="p"
                color={isAhead ? SLATE : 'text.primary'}
                sx={{ fontWeight: 700, lineHeight: 1.3 }}
              >
                {step.label}
              </Text>
              {step.hint && (
                <Text
                  variant="body2"
                  color={SLATE}
                  component="p"
                  sx={{ mt: 0.25 }}
                >
                  {step.hint}
                </Text>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
